// Apple Store–style bento grid shared by the games and Sigal pages:
// white cards with a "product shot" area, a tall feature card, an optional full-width card,
// and the tvOS hover: the card tilts toward the pointer 1:1, a soft sheen follows it, the art lifts.
import React from "react";
import {Link} from "react-router-dom";
import styled from "styled-components";
import {Reveal, useTilt} from "./ui";

export const BentoGrid = ({items}) => {
    const tilt = useTilt(5);
    return (
        <Grid>
            {items.map((it, i) => {
                const cls = it.feature ? 'feature' : it.wide ? 'wide' : undefined;
                const body = (
                    <>
                        <Shot style={{background: it.wash}}>
                            <div className="art">{it.art}</div>
                        </Shot>
                        <Meta>
                            <Label style={{color: it.tint}}>{it.eyebrow}</Label>
                            <Name dir={it.ltr ? 'ltr' : 'rtl'}>{it.title}</Name>
                            <Sub>{it.desc}</Sub>
                            <Open>{it.cta || 'פתח'} <span aria-hidden="true">‹</span></Open>
                        </Meta>
                    </>
                );
                const link = it.to
                    ? {as: Link, to: it.to}
                    : {href: it.href, ...(it.external ? {target: '_blank', rel: 'noopener noreferrer'} : {})};
                return (
                    <Reveal key={it.key} delay={Math.min(i, 5) * 50} className={cls}>
                        <Card {...link} {...tilt} className={cls} aria-label={it.title}>{body}</Card>
                    </Reveal>
                );
            })}
        </Grid>
    );
};

/* ------------------------------------------------------------------ styles */
// minmax(0, …): a 1fr column would otherwise grow to fit its widest artwork and push the grid
// past the screen edge on phones; the art scales inside the card instead
const Grid = styled.div`
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    & > * { min-width: 0; }
    & > .feature, & > .wide { grid-column: 1 / -1; }

    @media (min-width: 640px) { gap: 16px; }
    @media (min-width: 980px) {
        grid-template-columns: minmax(0, 1.15fr) minmax(0, 1fr) minmax(0, 1fr);
        grid-auto-rows: 330px;
        gap: 20px;
        & > .feature { grid-column: 1; grid-row: span 2; }
        & > .wide { grid-column: 1 / -1; }
        & > div { min-height: 0; }
    }
`;

const Card = styled.a`
    position: relative;
    min-width: 0;
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 300px;
    box-sizing: border-box;
    padding: 14px;
    border-radius: 22px;
    background: var(--surface);
    color: var(--label);
    text-decoration: none;
    isolation: isolate;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04), 0 4px 16px rgba(0, 0, 0, 0.04);
    transform: perspective(1100px) rotateX(var(--rx, 0deg)) rotateY(var(--ry, 0deg));
    transition: transform 520ms var(--ease), box-shadow 520ms var(--ease);
    -webkit-tap-highlight-color: transparent;

    /* sheen that follows the pointer */
    &::after {
        content: "";
        position: absolute;
        inset: 0;
        z-index: 3;
        border-radius: inherit;
        pointer-events: none;
        background: radial-gradient(55% 55% at var(--mx, 50%) var(--my, 0%), rgba(255, 255, 255, 0.5), transparent 70%);
        mix-blend-mode: soft-light;
        opacity: 0;
        transition: opacity 400ms ease;
    }

    @media (hover: hover) and (pointer: fine) {
        &:hover {
            transform: perspective(1100px) translateY(-6px) rotateX(var(--rx, 0deg)) rotateY(var(--ry, 0deg));
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05), 0 26px 50px rgba(0, 0, 0, 0.14);
        }
        /* while the pointer is over it, follow 1:1 — easing here would feel laggy */
        &.tracking { transition: transform 60ms linear, box-shadow 520ms var(--ease); }
        &:hover::after { opacity: 1; }
        &:hover .art { transform: translateY(-8px) scale(calc(var(--art, 1) * 1.07)); }
        &:hover .rotor { transform: rotate(200deg); }
    }
    &:active {
        transform: perspective(1100px) rotateX(var(--rx, 0deg)) rotateY(var(--ry, 0deg)) scale(0.98);
        transition-duration: 90ms;
    }
    &:focus-visible { outline: 3px solid var(--tint); outline-offset: 3px; }

    &.feature { min-height: 360px; --art: 1.25; }
    &.wide { min-height: 300px; }

    @media (max-width: 639px) {
        min-height: 236px;
        padding: 10px;
        border-radius: 18px;
        --art: 0.6;
        &.feature, &.wide { --art: 1; min-height: 330px; }
    }
    @media (min-width: 640px) { --art: 0.8; }
    @media (min-width: 980px) {
        min-height: 0;
        --art: 0.72;
        &.feature { min-height: 0; --art: 1.25; }
        &.wide { min-height: 0; flex-direction: row; gap: 8px; --art: 1.35; }
    }
    @media (prefers-reduced-motion: reduce) {
        transform: none !important;
    }
`;

/* product "photo" area: the art on its own wash, like a product shot */
const Shot = styled.div`
    position: relative;
    flex: 1;
    min-height: 150px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 14px;
    overflow: hidden;

    .art {
        display: flex;
        align-items: center;
        justify-content: center;
        transform: scale(var(--art, 1));
        transition: transform 700ms var(--ease);
    }
    /* light catching the top edge of the shot */
    &::before {
        content: "";
        position: absolute;
        inset: 0;
        border-radius: inherit;
        box-shadow: inset 0 0.5px 0 rgba(255, 255, 255, 0.35), inset 0 0 0 0.5px rgba(0, 0, 0, 0.04);
        pointer-events: none;
    }
    @media (max-width: 639px) { min-height: 110px; border-radius: 12px; }
    @media (min-width: 980px) { ${Card}.wide & { flex: 1.5; } }
    @media (prefers-reduced-motion: reduce) { .art { transition: none; } }
`;

const Meta = styled.div`
    padding: 16px 8px 6px;
    text-align: right;

    @media (max-width: 639px) { padding: 12px 4px 4px; }
    @media (min-width: 980px) {
        ${Card}.wide & {
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: center;
            padding: 24px 28px;
        }
    }
`;

const Label = styled.div`
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.02em;
    margin-bottom: 4px;
`;

const Name = styled.div`
    font-size: 19px;
    font-weight: 700;
    line-height: 1.25;
    letter-spacing: -0.02em;
    text-align: right;
    unicode-bidi: plaintext;

    ${Card}.feature &, ${Card}.wide & { font-size: clamp(24px, 2.6vw, 34px); letter-spacing: -0.03em; }
    @media (max-width: 639px) { font-size: 16px; }
`;

const Sub = styled.div`
    margin-top: 4px;
    font-size: 14px;
    color: var(--label-2);
    ${Card}.feature &, ${Card}.wide & { font-size: 16px; }
`;

const Open = styled.div`
    margin-top: 10px;
    font-size: 15px;
    font-weight: 500;
    color: var(--tint);

    span { display: inline-block; transition: transform 300ms var(--ease); }
    ${Card}:hover & span { transform: translateX(-3px); }
`;
